/**
 * Utility functions for handling app embedding within an iframe.
 */
import { z } from 'zod';
import { embeddedMessageSchema } from './schemas';

type Message = z.infer<typeof embeddedMessageSchema>;

export const isAppEmbedded = () => window.parent !== window;

const sendParentMessage = (
  message: Exclude<Message, { type: 'CORSO_PARENT_LOCATION' }>,
) => {
  window.parent.postMessage(message, '*');
};

export const configureEmbedding = (root: HTMLElement) => {
  if (isAppEmbedded()) {
    const checkHeight = () => {
      const body = document.getElementById('app-body');
      // use parentElement of the body because it includes the padding to protect the shadows
      if (!body?.parentElement) return;

      if (root.offsetHeight === body.parentElement.offsetHeight) return;

      sendParentMessage({
        type: 'CORSO_HEIGHT_CHANGE',
        height: body.parentElement.offsetHeight,
      });
    };
    const observer = new MutationObserver(checkHeight);

    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
    });
    window.addEventListener('resize', checkHeight);
    document.body.addEventListener(
      'load',
      (e) => {
        if (e.target instanceof HTMLElement && e.target.tagName === 'IMG') {
          checkHeight();
        }
      },
      true,
    );
  }

  return root;
};

export const getParentLocation = () =>
  new Promise<string>((resolve, reject) => {
    const handleMessage = (event: MessageEvent) => {
      const parsed = embeddedMessageSchema.safeParse(event.data);

      if (!parsed.success || parsed.data.type !== 'CORSO_PARENT_LOCATION')
        return;

      window.removeEventListener('message', handleMessage);

      resolve(parsed.data.location);
    };

    window.addEventListener('message', handleMessage);

    sendParentMessage({
      type: 'CORSO_REQUEST_PARENT_LOCATION',
    });

    setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      reject(new Error('Timeout waiting for parent location'));
    }, 3000);
  });

export const navigateParent = (url: string) => {
  sendParentMessage({
    type: 'CORSO_REQUEST_NAVIGATION',
    url,
  });
};
